import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Grid,
  Typography,
  Chip,
  styled,
  IconButton,
  Box,
  Paper,
  Collapse,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  Avatar,
  TextField
} from '@mui/material';
import {
  Close,
  ExpandMore,
  ExpandLess,
  Home,
  SwapHoriz,
  AttachMoney,
  Notes,
  Edit
} from '@mui/icons-material';

// Paleta de cores do Branding
const colors = {
  primary: '#5AB9D3',
  primaryLight: '#6AC9E3',
  primaryDark: '#4AA5C0',
  secondary: '#101A3A',
  secondaryLight: '#1A2747',
  white: '#FFFFFF',
  black: '#000000',
  textLight: '#E2E8F0',
  accent: '#38A169',
  error: '#E53E3E',
  success: '#38A169'
};

// Criar tema com a paleta de cores
const premiumTheme = createTheme({
  palette: {
    primary: {
      main: colors.primary,
      light: colors.primaryLight,
      dark: colors.primaryDark,
      contrastText: colors.white
    },
    secondary: {
      main: colors.secondary,
      light: colors.secondaryLight
    },
    error: {
      main: colors.error
    },
    success: {
      main: colors.success
    },
    text: {
      primary: colors.white,
      secondary: colors.textLight
    },
    background: {
      default: colors.secondary,
      paper: colors.secondaryLight
    }
  },
  shape: {
    borderRadius: 8
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            color: colors.white,
            '& fieldset': {
              borderColor: colors.primaryLight,
            },
            '&:hover fieldset': {
              borderColor: colors.primary,
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.primary,
            },
          },
          '& .MuiInputLabel-root': {
            color: colors.textLight,
          },
        },
      },
    },
  },
});

// Componentes estilizados
const PremiumDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.shape.borderRadius * 3,
    padding: 0,
    minWidth: '650px',
    maxWidth: '800px',
    overflow: 'hidden',
    boxShadow: theme.shadows[10],
    background: `linear-gradient(160deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.light} 100%)`,
    color: theme.palette.text.primary,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      width: '100%',
      height: '100%',
      background: `radial-gradient(circle at 80% 20%, rgba(90, 185, 211, 0.15) 0%, transparent 70%)`,
      pointerEvents: 'none',
      zIndex: 0,
    },
  },
  '& .MuiDialogContent-root': {
    position: 'relative',
    zIndex: 1,
  },
}));

const PremiumDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(160deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.light} 100%)`,
  color: theme.palette.common.white, // Título em branco
  fontWeight: 'bold',
  padding: theme.spacing(2, 3),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  position: 'relative',
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
  },
}));

const DetailSectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginTop: theme.spacing(2),
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const StyledStatusBadge = styled(Chip)(({ theme }) => ({
  fontWeight: 'bold',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.5),
  fontSize: '0.75rem',
}));

const StyledPropertyTypeBadge = styled(Chip)(({ theme }) => ({
  fontWeight: '500',
}));

const ValueCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'center', 
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.contrastText,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center'
}));

const ObservationContent = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.grey[100],
  color: colors.black, // Texto das observações em preto
  position: 'relative',
  '& .edit-button': {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    opacity: 0,
    transition: 'opacity 0.2s ease'
  },
  '&:hover .edit-button': {
    opacity: 1
  }
}));

const RealocacaoDetailDialog = ({ 
  open, 
  onClose, 
  realocacao,
  statusOptions,
  propertyTypes,
  formatDate
}) => {
  const isMobile = useMediaQuery(premiumTheme.breakpoints.down('sm'));
  const [expandedSections, setExpandedSections] = useState({
    observation: true,
    originAddress: true,
    destinationAddress: true
  });
  const [isEditingObservation, setIsEditingObservation] = useState(false);
  const [observationText, setObservationText] = useState('');

  // Atualiza o estado quando a realocação muda
  useEffect(() => {
    if (realocacao) {
      setObservationText(realocacao.observation || '');
    }
  }, [realocacao]);

  if (!realocacao) return null;

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getStatusColor = (status) => {
    const statusObj = statusOptions.find(s => s.value === status);
    return statusObj?.color || 'default';
  };

  const getPropertyTypeColor = (type) => {
    const typeObj = propertyTypes.find(t => t.value === type);
    return typeObj?.color || 'default';
  };

  const handleEditObservation = () => {
    setIsEditingObservation(true);
  };

  const handleSaveObservation = () => {
    // Aqui você adicionaria a lógica para salvar a observação no backend
    setIsEditingObservation(false);
    // Atualize o estado ou faça uma chamada API para salvar
  };

  const handleCancelEdit = () => {
    setIsEditingObservation(false);
    setObservationText(realocacao.observation || '');
  };

  const renderBasicInfo = () => (
    <Box>
      <DetailSectionTitle variant="h6">
        <SwapHoriz fontSize="small" /> Informações Básicas
      </DetailSectionTitle>
      <Divider sx={{ mb: 2, borderColor: 'grey.300' }} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <List dense>
            <ListItem>
              <ListItemText 
                primary="Título" 
                secondary={realocacao.titleRelocation || 'N/A'} 
                primaryTypographyProps={{ color: 'text.primary' }}
                secondaryTypographyProps={{ 
                  sx: { fontWeight: 'medium', color: 'text.primary' } // Texto secundário em branco
                }}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Status" 
                secondary={
                  <StyledStatusBadge 
                    label={statusOptions.find(s => s.value === realocacao.status)?.label || realocacao.status}
                    color={getStatusColor(realocacao.status)}
                  />
                } 
                primaryTypographyProps={{ color: 'text.primary' }}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Cliente" 
                secondary={realocacao.clientName || 'N/A'} 
                primaryTypographyProps={{ color: 'text.primary' }}
                secondaryTypographyProps={{ 
                  sx: { fontWeight: 'medium', color: 'text.primary' } // Texto secundário em branco
                }}
              />
            </ListItem>
          </List>
        </Grid>
        <Grid item xs={12} sm={6}>
          <List dense>
            <ListItem>
              <ListItemText 
                primary="Tipo de Imóvel" 
                secondary={
                  <StyledPropertyTypeBadge 
                    label={propertyTypes.find(t => t.value === realocacao.propertyType)?.label || realocacao.propertyType}
                    color={getPropertyTypeColor(realocacao.propertyType)}
                  />
                } 
                primaryTypographyProps={{ color: 'text.primary' }}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Categoria" 
                secondary={realocacao.category || 'N/A'} 
                primaryTypographyProps={{ color: 'text.primary' }}
                secondaryTypographyProps={{ 
                  sx: { fontWeight: 'medium', color: 'text.primary' } // Texto secundário em branco
                }}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Aceita Pets" 
                secondary={realocacao.petFriendly ? 'Sim' : 'Não'} 
                primaryTypographyProps={{ color: 'text.primary' }}
                secondaryTypographyProps={{ 
                  sx: { fontWeight: 'medium', color: 'text.primary' } // Texto secundário em branco
                }}
              />
            </ListItem>
          </List>
        </Grid>
      </Grid>
    </Box>
  );

  const renderValues = () => (
    <Box>
      <DetailSectionTitle variant="h6">
        <AttachMoney fontSize="small" /> Valores e Datas
      </DetailSectionTitle>
      <Divider sx={{ mb: 2, borderColor: 'grey.300' }} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <ValueCard elevation={0}>
            <Typography variant="subtitle2">
              Custo Estimado
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {realocacao.cost ? `€ ${realocacao.cost.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : 'N/A'}
            </Typography>
          </ValueCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <ValueCard elevation={0}>
            <Typography variant="subtitle2">
              Data Início
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {formatDate(realocacao.startDate) || 'N/A'}
            </Typography>
          </ValueCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <ValueCard elevation={0}>
            <Typography variant="subtitle2">
              Data Término
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {formatDate(realocacao.expectedEndDate) || 'N/A'}
            </Typography>
          </ValueCard>
        </Grid>
      </Grid>
    </Box>
  );

  const renderAddress = (address, label) => (
    <Box>
      <DetailSectionTitle variant="h6">
        <Home fontSize="small" /> {label}
        <IconButton 
          size="small" 
          onClick={() => toggleSection(label === 'Origem' ? 'originAddress' : 'destinationAddress')}
          sx={{ ml: 'auto', color: 'primary.main' }}
        >
          {expandedSections[label === 'Origem' ? 'originAddress' : 'destinationAddress'] ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </DetailSectionTitle>
      <Divider sx={{ mb: 2, borderColor: 'grey.300' }} />
      <Collapse in={expandedSections[label === 'Origem' ? 'originAddress' : 'destinationAddress']}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Logradouro" 
                  primaryTypographyProps={{ color: 'text.primary' }}
                  secondary={`${address?.street || 'N/A'}, ${address?.number || 'S/N'}`} 
                  secondaryTypographyProps={{ 
                    sx: { fontWeight: 'medium', color: 'text.primary' } // Texto secundário em branco
                  }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Bairro" 
                  primaryTypographyProps={{ color: 'text.primary' }}
                  secondary={address?.neighborhood || 'N/A'} 
                  secondaryTypographyProps={{ 
                    sx: { fontWeight: 'medium', color: 'text.primary' } // Texto secundário em branco
                  }}
                />
              </ListItem>
            </List>
          </Grid>
          <Grid item xs={12} sm={6}>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Cidade/Estado" 
                  primaryTypographyProps={{ color: 'text.primary' }}
                  secondary={`${address?.city || 'N/A'}/${address?.state || 'N/A'}`} 
                  secondaryTypographyProps={{ 
                    sx: { fontWeight: 'medium', color: 'text.primary' } // Texto secundário em branco
                  }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="País" 
                  primaryTypographyProps={{ color: 'text.primary' }}
                  secondary={address?.country || 'N/A'} 
                  secondaryTypographyProps={{ 
                    sx: { fontWeight: 'medium', color: 'text.primary' } // Texto secundário em branco
                  }}
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </Collapse>
    </Box>
  );

  const renderObservation = () => {
    // Mostrar a seção se houver observação ou se estiver em modo de edição
    const shouldShow = realocacao.observation || isEditingObservation || observationText;
    if (!shouldShow) return null;

    return (
      <Box>
        <DetailSectionTitle variant="h6">
          <Notes fontSize="small" /> Observações
          <IconButton 
            size="small" 
            onClick={() => toggleSection('observation')}
            sx={{ ml: 'auto', color: 'primary.main' }}
          >
            {expandedSections.observation ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </DetailSectionTitle>
        <Divider sx={{ mb: 2, borderColor: 'grey.300' }} />
        <Collapse in={expandedSections.observation}>
          {isEditingObservation ? (
            <Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={observationText}
                onChange={(e) => setObservationText(e.target.value)}
                variant="outlined"
                sx={{ mb: 2 }}
              />
              <Box display="flex" justifyContent="flex-end" gap={1}>
                <Button 
                  variant="outlined" 
                  onClick={handleCancelEdit}
                  color="primary"
                >
                  Cancelar
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleSaveObservation}
                  color="primary"
                >
                  Salvar
                </Button>
              </Box>
            </Box>
          ) : (
            <ObservationContent elevation={0}>
              <IconButton 
                className="edit-button"
                size="small" 
                onClick={handleEditObservation}
                color="primary"
              >
                <Edit fontSize="small" />
              </IconButton>
              <Typography variant="body2" whiteSpace="pre-wrap" sx={{ color: colors.black }}>
                {realocacao.observation || observationText || 'Nenhuma observação cadastrada'}
              </Typography>
            </ObservationContent>
          )}
        </Collapse>
      </Box>
    );
  };

  return (
    <ThemeProvider theme={premiumTheme}>
      <PremiumDialog 
        open={open} 
        onClose={onClose}
        fullScreen={isMobile}
        maxWidth="lg"
      >
        <PremiumDialogTitle>
          <Box display="flex" alignItems="center">
            <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
              <SwapHoriz />
            </Avatar>
            <Typography variant="h6" sx={{ color: colors.white }}>
              {realocacao.titleRelocation || 'Detalhes da Realocação'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} color="inherit" size="small">
            <Close />
          </IconButton>
        </PremiumDialogTitle>
        
        <DialogContent dividers sx={{ backgroundColor: 'background.default' }}>
          <Box sx={{ pt: 2 }}>
            {renderBasicInfo()}
            {renderValues()}
            {renderAddress(realocacao.originAddress, 'Origem')}
            {renderAddress(realocacao.destinationAddress, 'Destino')}
            {renderObservation()}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 2, 
          justifyContent: 'flex-end',
          backgroundColor: 'background.default'
        }}>
          <Button 
            onClick={onClose} 
            variant="contained" 
            color="primary"
            sx={{ 
              minWidth: '120px',
              '&:hover': {
                backgroundColor: 'primary.dark'
              }
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </PremiumDialog>
    </ThemeProvider>
  );
};

export default RealocacaoDetailDialog;